// Master-only: focused 4-step wizard for connecting the portal to a
// UniFi controller. Covers Stitch v2 design `ubiquiti_api_setup_wizard`
// (PNG-only mockup; copy derived).
//
// Reuses existing endpoints:
//   POST /api/v1/setup/probe-unifi  → probe step
//   PUT  /api/v1/setup/settings     → save step
//
// No new backend.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';

type Step = 'url' | 'key' | 'probe' | 'save' | 'done';

const STEP_LABEL: Record<Step, string> = {
  url: 'Controller URL',
  key: 'API key',
  probe: 'Probe',
  save: 'Save',
  done: 'Done',
};

const STEP_ORDER: ReadonlyArray<Exclude<Step, 'done'>> = ['url', 'key', 'probe', 'save'];

interface ProbeResult {
  ok: boolean;
  detail: string;
}

export function UbiquitiApiSetupWizardPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('url');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';

  useEffect(() => {
    if (state.status === 'authed' && !isMaster) {
      navigate('/', { replace: true });
    }
  }, [state, isMaster, navigate]);

  if (state.status !== 'authed' || !isMaster) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  function gotoKey(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!baseUrl.startsWith('https://')) {
      setErr('Controller URL must start with https://');
      return;
    }
    setStep('key');
  }

  function gotoProbe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setProbeResult(null);
    setStep('probe');
  }

  async function probe() {
    setBusy(true);
    setErr(null);
    try {
      const res = await apiClient.post<{ data: ProbeResult }>(
        '/api/v1/setup/probe-unifi',
        { base_url: baseUrl, api_key: apiKey },
        { skipTenantScope: true },
      );
      setProbeResult(res.data);
      if (res.data.ok) setStep('save');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Probe failed');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.put(
        '/api/v1/setup/settings',
        { unifi_base_url: baseUrl, unifi_api_key: apiKey },
        { skipTenantScope: true },
      );
      setStep('done');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-xl">
      <header>
        <h1 className="text-2xl font-semibold">UniFi API setup wizard</h1>
        <p className="text-on-surface-variant">
          Connect the portal to your UniFi controller in four steps.
        </p>
      </header>

      <ol className="flex gap-3 text-sm" aria-label="Wizard progress">
        {STEP_ORDER.map((s, i) => {
          const done = step === 'done' || STEP_ORDER.indexOf(s) < STEP_ORDER.indexOf(step as Exclude<Step, 'done'>);
          const active = s === step;
          return (
            <li
              key={s}
              className={
                active
                  ? 'font-bold text-primary'
                  : done
                    ? 'text-primary'
                    : 'text-on-surface-variant'
              }
            >
              {i + 1}.{' '}
              {/* `s` is a typed-literal from STEP_ORDER, not user input. */}
              {/* eslint-disable-next-line security/detect-object-injection */}
              {STEP_LABEL[s]}
              {done ? ' ✓' : ''}
            </li>
          );
        })}
      </ol>

      <Card>
        <CardBody>
          {step === 'url' && (
            <form onSubmit={gotoKey} className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="wiz-url" className="font-medium">
                  Controller base URL
                </label>
                <input
                  id="wiz-url"
                  type="url"
                  required
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://192.168.1.1"
                  pattern="^https://.+"
                  className="rounded border border-outline px-3 py-2"
                />
                <p className="text-sm text-on-surface-variant">
                  HTTPS required. The portal stores the URL and uses it for every
                  controller call.
                </p>
              </div>
              <div>
                <Button type="submit">Next</Button>
              </div>
            </form>
          )}

          {step === 'key' && (
            <form onSubmit={gotoProbe} className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="wiz-key" className="font-medium">
                  API key
                </label>
                <input
                  id="wiz-key"
                  type="password"
                  required
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="rounded border border-outline px-3 py-2"
                />
                <p className="text-sm text-on-surface-variant">
                  Generate in UniFi Settings → System → API. The key is encrypted at
                  rest and never sent to the browser.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setStep('url')}>
                  Back
                </Button>
                <Button type="submit">Next</Button>
              </div>
            </form>
          )}

          {step === 'probe' && (
            <div className="grid gap-4">
              <p>Test the connection to verify the URL and API key reach the controller.</p>
              {probeResult ? (
                <p className={probeResult.ok ? 'text-primary' : 'text-error'}>
                  <Icon name={probeResult.ok ? 'check_circle' : 'error'} /> {probeResult.detail}
                </p>
              ) : null}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setStep('key')} disabled={busy}>
                  Back
                </Button>
                <Button type="button" onClick={probe} disabled={busy}>
                  {busy ? <Spinner /> : 'Probe connection'}
                </Button>
              </div>
            </div>
          )}

          {step === 'save' && (
            <div className="grid gap-4">
              <p>
                <Icon name="check_circle" /> Probe succeeded. Save credentials to begin
                syncing hardware.
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setStep('probe')} disabled={busy}>
                  Back
                </Button>
                <Button type="button" onClick={save} disabled={busy}>
                  {busy ? <Spinner /> : 'Save credentials'}
                </Button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="grid gap-3">
              <p className="text-primary">
                <Icon name="check_circle" /> Credentials saved. Hardware will appear on
                the Hardware page once the next sync runs.
              </p>
              <div className="flex gap-2">
                <Link to="/hardware" className="text-primary underline">
                  Go to Hardware →
                </Link>
              </div>
            </div>
          )}

          {err ? (
            <p role="alert" className="text-error mt-4">
              <Icon name="error" /> {err}
            </p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
